/**
 * =============================================================================
 * BACKEND CONFIGURATION — the one place to change which backend is live
 * =============================================================================
 *
 * This is the single config location for the whole backend layer: which adapter
 * to use, and the URL each one needs.
 *
 * It is a separate module from ./index.js on purpose. index.js imports the
 * adapters, and the adapters need the URLs — if the URLs lived in index.js that
 * would be a circular import. Keeping config in its own leaf module (it imports
 * nothing) means everyone can read it and the cycle never forms.
 *
 * -----------------------------------------------------------------------------
 * HOW TO SWITCH BACKENDS
 * -----------------------------------------------------------------------------
 * Set REACT_APP_BACKEND_ADAPTER in the frontend's environment:
 *
 *     REACT_APP_BACKEND_ADAPTER=fastapi      (default)
 *     REACT_APP_BACKEND_ADAPTER=appsscript
 *
 * and supply the URL that adapter needs:
 *
 *     fastapi     → REACT_APP_BACKEND_URL      e.g. https://api.example.com
 *                   (the adapter appends /api itself)
 *     appsscript  → REACT_APP_APPS_SCRIPT_URL  the /exec URL of the deployed
 *                   Web App
 *
 * ⚠️ These are Create React App variables, INLINED AT BUILD TIME. Changing them
 * in Netlify and redeploying without rebuilding does nothing — the old value is
 * already baked into the bundle. You must trigger a fresh build.
 *
 * ⚠️ Anything named REACT_APP_* is PUBLIC. It ships inside the JavaScript every
 * visitor downloads. Never put a secret, key or password here. An Apps Script
 * /exec URL is effectively public by design; treat it as a published endpoint,
 * not as a credential.
 */

/** Adapter identifiers. Values are what you set REACT_APP_BACKEND_ADAPTER to. */
export const ADAPTERS = {
  FASTAPI: "fastapi",
  APPS_SCRIPT: "appsscript",
};

/**
 * Which adapter is active.
 *
 * Defaults to FastAPI because that is the backend that exists in this repo
 * (/backend), so a checkout with no environment configured behaves the way the
 * project reads. Compared case-insensitively and trimmed so a stray space or a
 * capital letter in a hosting dashboard does not silently fall through to the
 * default.
 */
const raw = (process.env.REACT_APP_BACKEND_ADAPTER || "").trim().toLowerCase();
export const ACTIVE_ADAPTER = raw === ADAPTERS.APPS_SCRIPT ? ADAPTERS.APPS_SCRIPT : ADAPTERS.FASTAPI;

/**
 * Base origin of the FastAPI service. The adapter appends `/api`.
 *
 * Left as an empty string rather than the literal "undefined" when unset — the
 * previous code built `${process.env.REACT_APP_BACKEND_URL}/api`, which with no
 * value produced the string "undefined/api" and sent requests to a nonsense
 * relative path. An empty value is now detected and reported as NOT_CONFIGURED.
 */
export const FASTAPI_BASE_URL = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/+$/, "");

/** Full /exec URL of the deployed Apps Script Web App. */
export const APPS_SCRIPT_URL = (process.env.REACT_APP_APPS_SCRIPT_URL || "").trim();

/**
 * Shared secret the Apps Script backend checks before doing anything.
 *
 * Must match the `SHARED_SECRET` Script Property on the deployed Web App exactly.
 *
 * ⚠️ THIS IS NOT A CREDENTIAL, and it is important to be clear about that. Like
 * every REACT_APP_* value it is inlined into the bundle at build time, so anyone
 * can read it out of the page source. It stops casual discovery of the /exec URL
 * turning into junk in the owners' Sheet and inbox. It is not authentication,
 * and it cannot be — a public form with no server of its own has nowhere to keep
 * a real one. The honeypot works alongside it for that reason.
 *
 * Unset here and the adapter refuses to send at all — see the guard in
 * ./appsScript.js and the reasoning there. That is deliberate: an empty secret
 * would otherwise fail in the worst possible way.
 */
export const APPS_SCRIPT_SECRET = (process.env.REACT_APP_APPS_SCRIPT_SECRET || "").trim();
